import * as domUtil from '../../util/dom';
import * as device from '../../util/device';
import * as videoAttributes from '../../util/video-attributes';

const CAPTIONS_STORAGE_KEY = "captionsOn";
const NAMESPACE = 'hlssubtitles';
const fullscreenEvents = 'fullscreenchange, mozfullscreenchange, MSFullscreenChange, webkitfullscreenchange';

const isVisible = () => domUtil.getStoredData(CAPTIONS_STORAGE_KEY) === 'true';

const cueToHtmlString = (cue) => {
    const lines = cue.text.split('\n').map((line) => {
        if (line.indexOf('<') !== 0) {
            line = '<span>' + line + '</span>';
        }

        return line;
    });

    const str = lines.join(lines.length > 1 ? '<br>' : '');

    return str.replace(/<c\.(.*?)>/g, '<span class="$1">').replace(/<\/c\.(.*?)>/g, '</span>');
};

const getCurrentInstanceNamespace = (videoElement) => {
    const uid = videoAttributes.getUID(videoElement) || videoAttributes.generateAndSetUID(videoElement);

    return `${NAMESPACE}-${uid}`;
};

/**
 * Abstract class for HLS subtitles. HLS.js and native HLS have their own implementations of hide() and show()
 */
export default class HlsSubtitles {
    constructor(videoElement, api) {
        this.videoElement = videoElement;
        this.api = api;

        this.activeTextTrack = null;

        this.domElement = null;
        this.domRegionElement = null;

        this.instanceNamespace = getCurrentInstanceNamespace(videoElement);

        this._setupInitialEventListeners();
    }

    _onSizeChanged() {
        if (!this.domElement) {
            return;
        }

        const fontSize = Math.max(this.videoElement.offsetWidth * 0.0375, 15);
        this.domElement.style.fontSize = `${fontSize}px`;
    }

    _setupInitialEventListeners() {
        domUtil.addEvents(this.videoElement.textTracks, 'addtrack', this._onAddTrack.bind(this), NAMESPACE);

        this.api.on('subtitles.controls', this._onControls.bind(this));

        domUtil.addEvents(this.videoElement.textTracks, 'change', this._updateSubtitleElementVisibility.bind(this), NAMESPACE);

        domUtil.addEvents(window, 'resize', this._onSizeChanged.bind(this), this.instanceNamespace);
        domUtil.addEvents(document, fullscreenEvents, this._onSizeChanged.bind(this), this.instanceNamespace);
    }

    _createSubtitlesElement() {
        this.domElement = document.createElement('div');
        this.domElement.className = 'hls-captions';

        this.domRegionElement = document.createElement('div');
        this.domRegionElement.className = 'region';
        this.domElement.appendChild(this.domRegionElement);

        if (this.activeTextTrack) {
            this.domElement.style.display = this.activeTextTrack.mode === 'hidden' ?
                'block' : 'none';
        }

        this._onSizeChanged();

        this.videoElement.parentNode.appendChild(this.domElement);
    }

    _findTextTrack() {
        for (let i = 0; i < this.videoElement.textTracks.length; i++) {
            const textTrack = this.videoElement.textTracks[i];
            if (textTrack.language === 'sv') {
                return textTrack;
            }
        }

        return null;
    }

    _onAddTrack() {
        const textTrack = this._findTextTrack();
        if (!textTrack) {
            return;
        }

        this.api.fire('captionsloaded');

        this.activeTextTrack = textTrack;

        if (isVisible()) {
            this.show();
        } else {
            this.hide();
        }

        // In iOS, the browser will render the subtitles itself
        if (device.isIOS()) {
            return;
        }

        if (!this.domElement) {
            this._createSubtitlesElement();
        }

        domUtil.addEvents(this.activeTextTrack, 'cuechange', this._onCueChange.bind(this), NAMESPACE);
    }

    _onControls(controlsEvent) {
        if (this.domElement) {
            domUtil.toggleClass(this.domElement, 'elevated', controlsEvent.detail.visible);
        }
    }

    _onCueChange(cueEvent) {
        const activeCues = cueEvent.target.activeCues;

        if (activeCues.length > 0 && this.activeTextTrack.mode === 'hidden') {
            const activeCue = activeCues[0];
            const activeCueLine = activeCue.line || 14; // line is not supported by Edge and Safari 7.

            this.domRegionElement.innerHTML = cueToHtmlString(activeCue);
            this.domRegionElement.style.top = `calc(100% / 16 * ${activeCueLine - 1})`;
        } else {
            this.domRegionElement.innerHTML = '';
        }
    }

    _updateSubtitleElementVisibility() {
        if (!this.activeTextTrack) {
            return;
        }

        // Keep local storage in sync with native player
        if (device.isIOS()) {
            const visible = this.activeTextTrack.mode === 'showing';

            if (visible) {
                domUtil.setStoredData(CAPTIONS_STORAGE_KEY, true);
            } else {
                domUtil.setStoredData(CAPTIONS_STORAGE_KEY, false);
            }

            return;
        }

        // In Safari, the text track mode may be changed by the browser to 'showing'.
        // This make sure the text track mode is synced with local storage.
        if (isVisible()) {
            this.show();
        } else {
            this.hide();
        }

        // The textTrack mode can either be disabled, hidden, or showing. We should
        // enable the custom subtitles element when textTrack is set to hidden.

        if (this.domElement) {
            this.domElement.style.display = this.activeTextTrack.mode === 'hidden' ?
                'block' : 'none';
        }
    }

    initialize() {
        if (this.videoElement.textTracks.length) {
            this._onAddTrack();
        }
    }

    show() {
        throw new Error('HlsSubtitles is abstract and `show` must be implemented');
    }

    hide() {
        throw new Error('HlsSubtitles is abstract and `hide` must be implemented');
    }

    isEnabled() {
        return Boolean(this.activeTextTrack);
    }

    detach() {
        if (this.domElement && this.domElement.parentNode) {
            this.domElement.parentNode.removeChild(this.domElement);
        }

        this.domElement = null;

        if (this.activeTextTrack) {
            domUtil.removeEvents(this.activeTextTrack, 'cuechange', NAMESPACE);
        }

        if (this.videoElement.textTracks) {
            domUtil.removeEvents(this.videoElement.textTracks, 'addtrack, change', NAMESPACE);
        }

        domUtil.removeEvents(window, 'resize', this.instanceNamespace);
        domUtil.removeEvents(document, fullscreenEvents, this.instanceNamespace);
    }
}



// WEBPACK FOOTER //
// ./src/js/modules/hls/hls-subtitles.js